import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteDepartment, useSaveDepartment } from '../../mutations';
import { useDepartments, useMembers } from '../../queries';
import type { Department } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Input } from '@/shared/ui/atoms/Input';
import { Select } from '@/shared/ui/atoms/Select';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import '../RolesManager/RolesManager.styl';

const valid = (name: string) => name.trim().length >= 2 && name.trim().length <= 80;

/**
 * The organisation's departments with their area lead (any active member). The lead sees and reviews the
 * department's projects; members and projects belong to a department so work is measurable per area.
 */
export function DepartmentsManager() {
  const { t } = useTranslation(undefined, { keyPrefix: 'departments' });
  const departments = useDepartments();
  const save = useSaveDepartment();
  const toast = useToast();
  const [name, setName] = useState('');
  const [lead, setLead] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (departments.isPending) return <Spinner label={t('loading')} />;

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(name)) return setError(t('nameInvalid'));
    if (!lead) return setError(t('leadRequired'));
    setError(null);
    save.mutate(
      { name: name.trim(), leadUserId: lead },
      {
        onSuccess: () => {
          setName('');
          setLead('');
          toast.show(t('created'), 'success');
        },
        onError: (err) => setError(errorMessage(err, t('saveError')))
      }
    );
  };

  return (
    <div className="f-roles">
      <p className="f-roles__hint">{t('intro')}</p>
      <form className="f-roles__bar" onSubmit={add} noValidate>
        <Input icon="office" aria-label={t('name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('placeholder')} />
        <LeadSelect id="dep-new-lead" value={lead} onChange={setLead} />
        <Button variant="add" type="submit" size="sm" iconLeft={<FontIcon name="plus-circle" />} loading={save.isPending}>
          {t('add')}
        </Button>
      </form>
      {error ? (
        <div className="f-newtask__error" role="alert">
          {error}
        </div>
      ) : null}
      {(departments.data ?? []).length === 0 ? (
        <p className="f-roles__hint">{t('empty')}</p>
      ) : (
        <ul className="f-roles__list">
          {(departments.data ?? []).map((d) => (
            <DepartmentRow key={d.id} department={d} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LeadSelect({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'departments' });
  const members = useMembers();
  return (
    <Select icon="user-tie" id={id} aria-label={t('lead')} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>
        {t('chooseLead')}
      </option>
      {(members.data?.items ?? []).filter((m) => m.status === 'ACTIVE').map((m) => (
        <option key={m.id} value={m.id}>
          {m.displayName}
        </option>
      ))}
    </Select>
  );
}

function DepartmentRow({ department }: { department: Department }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'departments' });
  const save = useSaveDepartment();
  const remove = useDeleteDepartment();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(department.name);
  const [lead, setLead] = useState(department.leadUserId);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(name)) return toast.show(t('nameInvalid'), 'error');
    save.mutate(
      { id: department.id, name: name.trim(), leadUserId: lead },
      {
        onSuccess: () => {
          setEditing(false);
          toast.show(t('saved'), 'success');
        },
        onError: (err) => toast.show(errorMessage(err, t('saveError')), 'error')
      }
    );
  };

  if (editing) {
    return (
      <li className="f-roles__item">
        <form className="f-roles__bar" onSubmit={submit} noValidate>
          <Input icon="office" aria-label={t('name')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <LeadSelect id={`dep-lead-${department.id}`} value={lead} onChange={setLead} />
          <Button type="submit" size="sm" loading={save.isPending}>
            {t('save')}
          </Button>
          <Button size="sm" variant="cancel" onClick={() => { setName(department.name); setLead(department.leadUserId); setEditing(false); }}>
            {t('cancel')}
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="f-roles__item">
      <div className="f-roles__main">
        <div className="f-roles__name">{department.name}</div>
        <div className="f-roles__meta">
          {t('leadIs', { name: department.leadName ?? '—' })} · {t('membersCount', { count: department.members })}
        </div>
      </div>
      <div className="f-roles__actions">
        <Button iconLeft={<FontIcon name="pencil" />} size="sm" variant="accent" onClick={() => setEditing(true)}>
          {t('edit')}
        </Button>
        {confirming ? (
          <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(department.id, { onSuccess: () => toast.show(t('deleted'), 'success'), onError: (err) => toast.show(errorMessage(err, t('deleteError')), 'error'), onSettled: () => setConfirming(false) })}>
            {t('confirmDelete')}
          </Button>
        ) : (
          <Button size="sm" variant="ghost" iconLeft={<FontIcon name="bin" />} onClick={() => setConfirming(true)}>
            {t('delete')}
          </Button>
        )}
      </div>
    </li>
  );
}
